import React from 'react';
import { Plus, Trash2, ChevronDown, ChevronUp } from 'react-feather';

const ParameterEditor = ({ parameters, onChange }) => {
  // The component's state is now derived directly from props.
  // This simplifies the logic and removes the need for the useEffect hook to sync state.
  const { properties = {}, required = [] } = (typeof parameters === 'string') 
    ? JSON.parse(parameters || '{}') 
    : (parameters || {});

  const [expandedProps, setExpandedProps] = React.useState({});

  const notifyParent = (newProperties, newRequired) => {
    onChange({
      type: 'object',
      properties: newProperties,
      required: newRequired,
    });
  };

  const toggleProperty = (propName) => {
    setExpandedProps(prev => ({ ...prev, [propName]: !prev[propName] }));
  };

  const addProperty = () => {
    const newPropName = `property_${Object.keys(properties).length + 1}`;
    const newProperties = {
      ...properties,
      [newPropName]: { type: 'string', description: '' }
    };
    
    setExpandedProps(prev => ({ ...prev, [newPropName]: true }));
    notifyParent(newProperties, required);
  };

  const removeProperty = (propName) => {
    const newProperties = { ...properties };
    delete newProperties[propName];
    const newRequired = required.filter(name => name !== propName);
    notifyParent(newProperties, newRequired);
  };

  // This is the core fix. It directly constructs the new state from the old one.
  const updateProperty = (oldName, field, newValue) => {
    let newProperties = { ...properties };
    let newRequired = [...required];

    if (field === 'name' && oldName !== newValue) {
      // RENAME LOGIC: Rebuild the properties object with the new key
      newProperties = Object.keys(properties).reduce((acc, key) => {
        acc[key === oldName ? newValue : key] = properties[key];
        return acc;
      }, {});
      newRequired = required.map(name => (name === oldName ? newValue : name));

      setExpandedProps(prev => {
        const next = { ...prev };
        next[newValue] = prev[oldName] ?? true;
        delete next[oldName];
        return next;
      });

    } else {
      // UPDATE LOGIC: Update the value of an existing property
      newProperties[oldName] = { ...properties[oldName], [field]: newValue };
    }
    
    notifyParent(newProperties, newRequired);
  };

  const toggleRequired = (propName) => {
    const newRequired = required.includes(propName)
      ? required.filter(name => name !== propName)
      : [...required, propName];
    notifyParent(properties, newRequired);
  };

  // The agent is handed object parameters as a JSON string, so the only thing
  // that keeps the shape predictable is showing it an example. This is free-form
  // JSON, any valid value is allowed, but it must parse, or the agent would be
  // shown a malformed example.
  const [exampleDrafts, setExampleDrafts] = React.useState({});
  const [exampleErrors, setExampleErrors] = React.useState({});

  // Read back the type each top-level field declares, so the user can see what
  // their own quoting choices actually mean before saving.
  const describeExampleTypes = (rawValue) => {
    let parsed;
    try {
      parsed = JSON.parse(rawValue);
    } catch {
      return null;
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;

    return Object.entries(parsed).map(([key, value]) => {
      let type;
      if (value === null) type = 'null';
      else if (Array.isArray(value)) type = 'array';
      else if (typeof value === 'object') type = 'object';
      else type = typeof value; // string | number | boolean
      return { key, type };
    });
  };

  const updateExample = (propName, rawValue) => {
    setExampleDrafts(prev => ({ ...prev, [propName]: rawValue }));

    if (!rawValue.trim()) {
      setExampleErrors(prev => ({ ...prev, [propName]: null }));
      const { example, ...rest } = properties[propName];
      notifyParent({ ...properties, [propName]: rest }, required);
      return;
    }

    try {
      JSON.parse(rawValue);
      setExampleErrors(prev => ({ ...prev, [propName]: null }));
      // Store the text as typed so the agent sees exactly this format.
      updateProperty(propName, 'example', rawValue);
    } catch (err) {
      setExampleErrors(prev => ({ ...prev, [propName]: 'Not valid JSON' }));
    }
  };

  return (
    <div className="parameter-editor">
      <div className="parameter-editor-header">
        <h4>Properties</h4>
        <button type="button" className="add-property-button" onClick={addProperty}>
          <Plus size={16} /> Add Property
        </button>
      </div>
      
      {Object.keys(properties).length === 0 ? (
        <div className="empty-properties">
          <p>No properties defined. Click "Add Property" to create one.</p>
        </div>
      ) : (
        <div className="properties-list">
          {Object.keys(properties).map(propName => (
            <div key={propName} className="property-item">
              <div className="property-header" onClick={() => toggleProperty(propName)}>
                <div className="property-name">{propName}</div>
                <div className="property-actions">
                  {expandedProps[propName] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
              </div>
              
              {expandedProps[propName] && (
                <div className="property-details">
                  <div className="property-field">
                    <label>Name</label>
                    <input
                      type="text"
                      defaultValue={propName}
                      onBlur={(e) => updateProperty(propName, 'name', e.target.value)}
                    />
                  </div>
                  <div className="property-field">
                    <label>Type</label>
                    <select value={properties[propName].type} onChange={(e) => updateProperty(propName, 'type', e.target.value)}>
                      <option value="string">String</option>
                      <option value="number">Number</option>
                      <option value="boolean">Boolean</option>
                      <option value="object">Object</option>
                      <option value="array">Array</option>
                    </select>
                  </div>
                  <div className="property-field">
                    <label>Description</label>
                    <input
                      type="text"
                      value={properties[propName].description || ''}
                      onChange={(e) => updateProperty(propName, 'description', e.target.value)}
                      placeholder="Describe this property"
                    />
                  </div>
                  <div className="property-field checkbox">
                    <label>
                      <input
                        type="checkbox"
                        checked={required.includes(propName)}
                        onChange={() => toggleRequired(propName)}
                      />
                      Required
                    </label>
                  </div>

                  {properties[propName].type === 'object' && (
                    <div className="property-field">
                      <label>Example Value (JSON)</label>
                      <textarea
                        className="object-example-input"
                        rows="4"
                        spellCheck="false"
                        value={
                          exampleDrafts[propName] !== undefined
                            ? exampleDrafts[propName]
                            : (properties[propName].example || '')
                        }
                        onChange={(e) => updateExample(propName, e.target.value)}
                        placeholder={'{\n  "first_name": "Ada",\n  "age": 36\n}'}
                      />
                      {exampleErrors[propName] && (
                        <p className="object-example-error">{exampleErrors[propName]}</p>
                      )}

                      {(() => {
                        const raw = exampleDrafts[propName] !== undefined
                          ? exampleDrafts[propName]
                          : (properties[propName].example || '');
                        const summary = describeExampleTypes(raw);
                        if (!summary || summary.length === 0) return null;
                        return (
                          <div className="object-example-types">
                            <span className="object-example-types-label">
                              This example declares:
                            </span>
                            {summary.map(({ key, type }) => (
                              <span key={key} className="object-example-type-chip">
                                {key}
                                <em className={`type-${type}`}>{type}</em>
                              </span>
                            ))}
                          </div>
                        );
                      })()}

                      <div className="object-example-hint">
                        <p>
                          The agent sends this parameter as a JSON string, and
                          <strong> quotes decide the type</strong>. Quote anything that
                          must stay text, phone numbers, ZIP codes, IDs with leading
                          zeros, or it arrives as a number and loses them.
                        </p>
                        <pre className="object-example-legend">
{`{
  "name":  "Ada",         `}<em className="type-string">string</em>{`
  "phone": "9812345678",  `}<em className="type-string">string</em>{`  ← quoted, stays text
  "zip":   "01234",       `}<em className="type-string">string</em>{`  ← unquoted would become 1234
  "age":   36,            `}<em className="type-number">number</em>{`
  "vip":   true           `}<em className="type-boolean">boolean</em>{`
}`}
                        </pre>
                      </div>
                    </div>
                  )}

                  <button type="button" className="remove-property-button" onClick={() => removeProperty(propName)}>
                    <Trash2 size={14} /> Remove Property
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ParameterEditor;